import React from "react";
import { Link } from "react-router-dom";
import { TrendUp, Warning, Robot } from "@phosphor-icons/react";
import { Card, Empty, Pill, aud, num, pct } from "./ui";

const RANKS = [["frequency", "Frequency"], ["total_cost", "Total cost"], ["avg_cost", "Avg cost"], ["growth", "Trend growth"], ["caravans", "Caravans affected"]];

export function TopFailures({ rows, rankBy, onRank }) {
  return (
    <Card title="Top 10 warranty failure points" icon={TrendUp} testId="wi-top-failures" actions={
      <div className="flex flex-wrap gap-1">
        {RANKS.map(([k, l]) => (
          <button key={k} onClick={() => onRank(k)} data-testid={`wi-rank-${k}`}
            className={`text-[0.7rem] rounded-full px-2.5 py-1 border ${rankBy === k ? "border-[#73bf44] text-white bg-[#73bf44]/10" : "border-neutral-800 text-neutral-500 hover:text-white"}`}>{l}</button>
        ))}
      </div>
    }>
      {!rows.length ? <Empty>No warranty faults for these filters.</Empty> : (
        <table className="w-full text-xs">
          <thead className="text-[0.6rem] uppercase tracking-wider text-neutral-500 text-left">
            <tr className="border-b border-neutral-800"><th className="py-2 pr-2">#</th><th className="py-2 pr-3">Component</th><th className="py-2 pr-3 text-right">Reports</th>
              <th className="py-2 pr-3 text-right">Caravans</th><th className="py-2 pr-3 text-right">Total cost</th><th className="py-2 pr-3 text-right">Avg cost</th>
              <th className="py-2 pr-3 text-right">90d trend</th><th className="py-2 pr-3">Models</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.system}-${r.component}`} className="border-b border-neutral-900" data-testid="wi-top-row">
                <td className="py-2.5 pr-2 text-neutral-600 tabular-nums">{i + 1}</td>
                <td className="py-2.5 pr-3"><div className="text-neutral-100 font-semibold">{r.component}</div><div className="text-neutral-600">{r.system}{r.safety_critical ? " · safety-critical reports" : ""}</div></td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-white">{num(r.frequency)}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-300">{num(r.caravans)}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-[#d9b45b]">{aud(r.total_cost)}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-300">{aud(r.avg_cost)}</td>
                <td className={`py-2.5 pr-3 text-right tabular-nums ${r.growth_pct > 0 ? "text-amber-400" : "text-neutral-500"}`}>{r.recent_90d} {r.growth_pct !== null ? `(${pct(r.growth_pct)})` : ""}</td>
                <td className="py-2.5 pr-3 text-neutral-400">{r.models.join(", ")}</td>
                <td className="py-2.5"><Link to={`/admin/reliability/report?system=${encodeURIComponent(r.system)}&component=${encodeURIComponent(r.component)}`}
                  className="text-[0.7rem] font-semibold text-[#73bf44] hover:underline whitespace-nowrap" data-testid="wi-top-report-link">Report →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export function Alerts({ alerts }) {
  return (
    <Card title="Emerging issues & engineering alerts" icon={Warning} testId="wi-alerts">
      {!alerts.length ? <Empty>No alerts above the configured thresholds.</Empty> : (
        <ul className="space-y-2">
          {alerts.map((a, i) => (
            <li key={i} className="flex items-start gap-2 text-xs" data-testid="wi-alert">
              <Pill tone={a.severity === "high" ? "red" : "amber"}>{a.kind === "spike" ? "Spike" : a.kind === "cost" ? "Cost" : "Model"}</Pill>
              <span className="text-neutral-300">{a.message}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function EnquiryCorrelation({ rows }) {
  return (
    <Card title="Technical Assistant enquiries vs warranty claims (30 days)" icon={Robot} testId="wi-enquiries">
      <p className="text-[0.7rem] text-neutral-500 mb-3">Aggregate topic comparison. Enquiries are early signals only — never treated as confirmed failures.</p>
      {!rows.length ? <Empty>No enquiry data in the last 60 days.</Empty> : (
        <table className="w-full text-xs">
          <thead className="text-[0.6rem] uppercase tracking-wider text-neutral-500 text-left"><tr className="border-b border-neutral-800">
            <th className="py-2 pr-3">System</th><th className="py-2 pr-3 text-right">Enquiries</th><th className="py-2 pr-3 text-right">Change</th><th className="py-2 pr-3 text-right">Claims</th><th className="py-2 pr-3 text-right">Change</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.system} className="border-b border-neutral-900">
                <td className="py-2 pr-3 text-neutral-200">{r.system}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{r.enquiries_30d}</td><td className="py-2 pr-3 text-right tabular-nums text-neutral-500">{pct(r.enquiry_growth_pct)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{r.claims_30d}</td><td className="py-2 pr-3 text-right tabular-nums text-neutral-500">{pct(r.claim_growth_pct)}</td>
                <td className="py-2">{r.emerging && <Pill tone="amber">Flag for technical review</Pill>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
